import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { IColumnGroup, IColumnMetaData } from '../report-data.service';
import { Dictionary, List } from '@type_definitions/vendor';
import _ from 'underscore';
import { util } from '../../util';

interface IExpandColumnGroup extends IColumnGroup {
  expanded?:boolean;
}

@Component({
  selector: 'select-report-columns',
  template: `
    <div class="select-report-columns margin-left-lg">
      <div class="select-report-columns-group" *ngFor="let group of groups">
        <div class="form-check checkbox">
          <label class="form-check-label-wrapper">
            <input class="form-check-input" type="checkbox"
              [indeterminate]="groupIndeterminate[group.code]"
              [(ngModel)]="groupSelected[group.code]"
              (change)="onGroupChange(group.code)">
          </label>

          <span class="group-header-toggle" (click)="toggleGroup(group)">
            <span><collapse-indicator class="collapse-indicator" [open]="group.expanded" [clickable]="true"></collapse-indicator></span>
            <span class="form-check-label group-header-text">{{group.description}}</span>
          </span>
        </div>
        <collapsible-pane [collapsed]="!group.expanded">
          <c-checkbox-list
            [key]="group.code + '_' + (isInDialog? 'dialog' : 'report')"
            [possibleValues]="columnsByGroup[group.code]"
            [values]="selectedColumnsByGroup[group.code]"
            [formatFunction]="formatColumn"
            (onSelectValue)="updateGroupSelection(group.code)"
            [columns]="checkboxColumns || 6"></c-checkbox-list>
        </collapsible-pane>
      </div>
    </div>
  `,
  styleUrls: ['./select-report-columns.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SelectReportColumnsComponent implements OnInit, OnChanges {
  @Input() public columns: IColumnMetaData[];
  @Input() public checkboxColumns: number;
  @Input() public columnKey: string;
  @Input() public isInDialog:boolean;
  @Output() public onSelectValue: EventEmitter<IColumnMetaData[]> = new EventEmitter<IColumnMetaData[]>();

  private defaultGroup: IColumnGroup = {
    code: 'OTHER',
    description: 'Other',
    sequence: 1000
  };

  private previousColumns:IColumnMetaData[];

  //list of column groups
  public groups:IExpandColumnGroup[];

  //dictionary of columns per group code
  public columnsByGroup:Dictionary<IColumnMetaData[]>;

  //dictionary of selected columns per group code
  public selectedColumnsByGroup:Dictionary<IColumnMetaData[]>;

  //dictionary of selection per group code
  public groupSelected:Dictionary<boolean> = {};

  //dictionary of indeterminate selection per group code
  public groupIndeterminate:Dictionary<boolean> = {};

  constructor() {
    /**/
  }

  public ngOnInit(): void {
    this.onChangeColumns();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.columns) {
      if (!_.isEqual(this.previousColumns, this.columns)) {
        this.onChangeColumns();
        this.previousColumns = util.deepClone(this.columns);
      }
    }
  }

  public formatColumn = (column: IColumnMetaData): string => {
    return column.formattedLabel;
  };

  public toggleGroup(group: IExpandColumnGroup): void {
    group.expanded = !group.expanded;
  }

  public onChangeColumns(): void {
    // when the full list of columns changes, we set up all the dictionaries that are used in the component
    const newColumns:IColumnMetaData[] = this.columns;
    this.groups = _.chain(newColumns).map(c => c.group || this.defaultGroup).uniq(g => g.code).sortBy('sequence').value();
    this.columnsByGroup = <any>_.chain(newColumns).groupBy(c => (c.group || this.defaultGroup).code).value(); //type conversion fails, so we cast to any knowing that underscore does return a Dictionary
    this.selectedColumnsByGroup = {};

    _.forEach(this.columnsByGroup, (columns:List<IColumnMetaData>, groupCode: string) => {
      this.selectedColumnsByGroup[ groupCode ] = _.filter(columns, c => util.getValue(c, this.columnKey));
    });

    if (this.groups.length === 1) {
      const firstGroup:IExpandColumnGroup = this.groups[ 0 ];
      firstGroup.expanded = true;
      if (firstGroup.code === this.defaultGroup.code) {
        this.defaultGroup.description = 'All';
      }
    }

    this.updateGroupSelection();
  }

  // changes the selection / indeterminate state at the group level
  public updateGroupSelection(groupCode?: string): void {
    const updateGroup = (groupCode: string): void => {
      this.groupSelected[ groupCode ] = !!this.selectedColumnsByGroup[ groupCode ].length;
      this.groupIndeterminate[ groupCode ] = this.selectedColumnsByGroup[ groupCode ].length > 0 && this.selectedColumnsByGroup[ groupCode ].length !== this.columnsByGroup[ groupCode ].length;
      _.forEach(
        this.columnsByGroup[ groupCode ],
        //toggle the xxxinlist boolean
        c => {
          util.setValue(c, this.columnKey, _.contains(this.selectedColumnsByGroup[ groupCode ], c));
        }
      );
    };

    if (groupCode) {
      updateGroup(groupCode);
    } else {
      _.forEach(this.groups, (g:IColumnGroup) => updateGroup(g.code));
    }

    this.onSelectValue.emit(_.chain(this.selectedColumnsByGroup).values().flatten().value());
  }

  // called when the group checkbox is changed
  public onGroupChange(groupCode:string) {
    if (this.groupSelected[ groupCode ]) {
      this.selectedColumnsByGroup[ groupCode ] = _.clone(this.columnsByGroup[ groupCode ]);
    } else {
      this.selectedColumnsByGroup[ groupCode ] = [];
    }
    this.groupIndeterminate[ groupCode ] = false;
    this.updateGroupSelection(groupCode);
  }
}